/*
 * cf-snap-to-buildable.js — Click Fencing — 2026-09-07
 *
 * Pete: "the errors that stop people progressing through to cart have to stop... if
 * something gets error rejection lets make it snap to what it needs to be."
 * And: "we can snap as far as it minimally needs to fit what they want. if they remove
 * something it should snap back down."
 *
 * WHAT WAS ALREADY THERE
 * ----------------------
 * All four calculators already carry a snap-up routine, written 2026-08-30 to Pete's
 * earlier instruction "make all calculators snap up to minimum". It probes the solver for
 * the shortest length it can actually build, sets the section to it, and explains itself.
 * blade.js:7166-7208 is the reference implementation.
 *
 * It is wired to ONE event: toggling the GATE on.
 *
 * So the customer path that dead-ends is the obvious one. Measured on blade, 2026-09-07,
 * driving the real controls:
 *   set 6000mm -> turn gate ON (fine) -> type 900mm
 *   => state stays 900, no snap, no popup, validateSection() false, preview reads
 *      "Enter section length to see preview", and Next says "Section 1 is incomplete."
 * Nothing re-checks buildability when the LENGTH changes, when the RAKE is toggled, or
 * when the rake HEIGHT changes.
 *
 * Measured dead bands this closes (blocked, with no explanation and no way forward):
 *   blade    gate+rake  below 2400mm   (32 lengths)
 *   perfmesh gate+rake  below 2400mm   (31 lengths)
 *   blade    rake       below 1400mm
 *   perfmesh rake       below 1400mm
 *   glass    rake       below 1350mm
 *   flat top rake       below 1300mm
 * Those minimums are the solver's own and agree with the James Glen ladder: blade rake is
 * 1252 wide and its gate 920 (1252 + 920 + 3 posts + clearance -> 2400); flat top rake is
 * 1200 and its gate 970.
 *
 * WHAT THIS DOES
 * --------------
 * Re-checks buildability whenever the length settles, the rake is toggled, or the rake
 * height changes — then applies the same snap-up the gate toggle already does, and tells
 * the customer what happened and why.
 *
 * Snap-back-down (Pete's rule): the length the customer actually TYPED is remembered. When
 * they later remove the gate or the rake, the section returns to their own number — or to
 * the new minimum if their number is still too short — so removing a component undoes the
 * stretch instead of leaving the fence long.
 *
 * DELIBERATE CHOICES
 * ------------------
 * - Settle only. It acts on change/blur, never on input. Normalising a field while someone
 *   is still typing is what broke the clamp quantity box (see cf-clamp-qty-input.js): "900"
 *   passes through "9" and "90", and a keystroke-time snap would fire on those.
 * - Probes the SOLVER, never a re-derived formula. Every gate defect found on 30 Aug came
 *   from a validator whose arithmetic had drifted from the solver's real rules. A probed
 *   target is drawable by definition.
 * - Probes a COPY of the section. Live state is never mutated to test a candidate.
 * - Snaps UP only (Pete, Q1). It never shortens a run the customer asked for, except to
 *   give back length this patch itself added.
 * - Re-entrancy is handled with an in-flight flag cleared in a finally, never a latch. A
 *   __cfSnapping guard that stuck true once disabled every later correction with no error.
 */
(function () {
  "use strict";

  var PATHS = /(blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence|glass-fencing)/;
  if (!PATHS.test(location.pathname)) return;
  if (window.__cfSnapToBuildable) return;
  window.__cfSnapToBuildable = true;

  var TRIES = 0, MAX_TRIES = 200;
  var MAX_SEARCH = 2000;   // mm above the first guess before we give up
  var busy = false;

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }
  function num(v) { var n = parseInt(v, 10); return isFinite(n) ? n : 0; }

  // ── Buildability oracle ───────────────────────────────────────────────────
  // Ask the calculator's OWN solver whether a candidate length produces a layout.
  // Each calculator exposes a different entry point; all three shapes are handled.
  function buildable(fc, section, len) {
    var probe = Object.assign({}, section, { length: String(len) });
    try {
      if (typeof fc.guaranteedSolve === "function") {          // blade
        if (fc.guaranteedSolve(probe)) return true;
      }
      if (typeof fc.calculateSpread === "function") {          // flat top, perfmesh
        var element = (probe.hasGate && probe.hasRaked) ? "gate_rake"
                    : probe.hasGate ? "gate"
                    : probe.hasRaked ? "rake" : "none";
        var post  = num(probe.gapSpacing) || 50;
        var sheet = num(probe.panelLength) || 3000;
        var gate  = num(probe.gateLength) || (typeof fc.getDefaultGateLength === "function" ? fc.getDefaultGateLength() : 970);
        var r = fc.calculateSpread(len, post, sheet, element, false, null, gate);
        // A real layout has a non-zero total, or is a valid element-only run (which the
        // solver marks by attaching its perfomesh block).
        if (r && (r.total > 0 || r.perfomesh)) return true;
      }
      if (typeof fc.peterCalculateLayout === "function") {     // glass
        var pl = num(probe.panelLength) || 1200;
        var gs = num(probe.gapSpacing) || 50;
        if (fc.peterCalculateLayout(probe, pl, gs)) return true;
      }
    } catch (e) {}
    return false;
  }

  // Smallest buildable length at or above `from`. Returns null if nothing fits.
  function minBuildable(fc, section, from) {
    var start = Math.max(100, from);
    for (var L = start; L <= start + MAX_SEARCH; L++) {
      if (buildable(fc, section, L)) return L;
    }
    return null;
  }

  function describe(section) {
    var bits = [];
    if (section.hasGate)  bits.push("a " + (num(section.gateLength) || "") + "mm gate");
    if (section.hasRaked) bits.push("a raked panel");
    if (!bits.length) return "this section";
    return bits.join(" and ");
  }

  // The shared popup is styled as an ERROR — red icon, "Validation Error" heading. Nothing
  // has gone wrong here: we fixed their section for them. Re-dress it so a helpful message
  // does not read like a telling-off (Pete, 2026-09-07: "make the wording nicer and not be
  // so forward"). Only touches the popup this patch itself raised.
  function tell(fc, msg, heading) {
    try {
      if (typeof fc.showValidationPopup === "function") {
        fc.showValidationPopup(msg);
        setTimeout(function () {
          try {
            var btn = document.getElementById("validation-ok-btn");
            if (!btn) return;
            var card = btn.closest("div[style*='border-radius']") || btn.parentElement;
            if (!card) return;
            var h = card.querySelector("h3");
            if (h && /validation error/i.test(h.textContent || "")) h.textContent = heading;
            var circle = card.querySelector("div[style*='border-radius: 50%']");
            if (circle) circle.style.background = "#eff6ff";   // calm blue, not alarm red
          } catch (e) {}
        }, 30);
        return;
      }
    } catch (e) {}
    try { console.log("[cf-snap] " + msg.replace(/\n+/g, " ")); } catch (e) {}
  }

  function setLength(fc, n, len) {
    fc.updateSectionData(n, "length", String(len));
    var input = document.getElementById("section-" + n + "-length");
    if (input) input.value = String(len);
    try { fc.updateCalculationResults(n); } catch (e) {}
  }

  // ── The check ─────────────────────────────────────────────────────────────
  function check(fc, n, why) {
    if (busy) return;
    busy = true;
    try {
      var s = fc.state && fc.state.sections && fc.state.sections[n];
      if (!s) return;
      var cur = num(s.length);
      if (!cur) return;                       // nothing entered yet — leave them alone

      // Remember what the customer actually asked for, before we ever change it.
      if (s.__cfTyped === undefined || why === "length") s.__cfTyped = cur;

      if (!buildable(fc, s, cur)) {
        var target = minBuildable(fc, s, cur);
        if (target === null) return;          // nothing fits — leave the existing message
        setLength(fc, n, target);
        s.__cfSnapped = true;
        tell(fc,
          "Your section was " + cur + "mm, which is too short for " + describe(s) + ".\n\n" +
          "We have set it to " + target + "mm — the shortest length this will actually build.",
          "We adjusted this section");
        return;
      }

      // Buildable already. If WE lengthened it earlier and the customer has since removed
      // whatever forced that, give their own number back (Pete: "if they remove something
      // it should snap back down"). Never shorten below what still builds.
      if (s.__cfSnapped) {
        var want = num(s.__cfTyped);
        if (want && want < cur && buildable(fc, s, want)) {
          setLength(fc, n, want);
          s.__cfSnapped = false;
          tell(fc, "That section is back to your " + want + "mm — the extra length is no longer needed.",
            "Back to your measurement");
        }
      }
    } catch (e) {
      try { console.warn("[cf-snap] check failed:", e && e.message); } catch (e2) {}
    } finally {
      busy = false;                            // always cleared — never a sticky latch
    }
  }

  function sectionOf(el) {
    var m = (el && el.id || "").match(/^section-(\d+)-/);
    return m ? parseInt(m[1], 10) : null;
  }

  function install() {
    var fc = inst();
    if (!fc || !fc.state) return false;
    var P = Object.getPrototypeOf(fc);
    if (!P || P.__cfSnapInstalled) return true;

    // Rake toggle — the gate toggle already snaps; the rake never did.
    if (typeof P.toggleRakedPanel === "function") {
      var oRake = P.toggleRakedPanel;
      P.toggleRakedPanel = function (n, checked) {
        var r = oRake.apply(this, arguments);
        var self = this;
        setTimeout(function () { check(self, n, "rake"); }, 60);
        return r;
      };
    }

    // Gate toggle — re-check AFTER its own snap, so gate+rake together is covered.
    if (typeof P.toggleGate === "function") {
      var oGate = P.toggleGate;
      P.toggleGate = function (n, checked) {
        var r = oGate.apply(this, arguments);
        var self = this;
        setTimeout(function () { check(self, n, "gate"); }, 60);
        return r;
      };
    }

    // Length field and rake height — settle events only, never on input.
    document.addEventListener("change", function (e) {
      var el = e.target;
      if (!el || !el.id) return;
      var n = sectionOf(el);
      if (n === null) return;
      if (/^section-\d+-(length|retained-height)$/.test(el.id)) {
        setTimeout(function () { check(inst(), n, /length/.test(el.id) ? "length" : "height"); }, 80);
      }
    }, true);

    document.addEventListener("blur", function (e) {
      var el = e.target;
      if (!el || !el.id) return;
      var n = sectionOf(el);
      if (n === null) return;
      if (/^section-\d+-length$/.test(el.id)) {
        setTimeout(function () { check(inst(), n, "length"); }, 80);
      }
    }, true);

    P.__cfSnapInstalled = true;
    try { console.log("[cf-snap-to-buildable] installed on " + location.pathname); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (!done && ++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryInstall);
  } else {
    tryInstall();
  }
})();
